import { createSlice } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';

// Single in-memory flag to prevent concurrent logouts
let isLoggingOut = false;
export const acquireLogoutLock = () => {
  if (isLoggingOut) return false;
  isLoggingOut = true;
  return true;
};
export const releaseLogoutLock = () => {
  isLoggingOut = false;
};

// Generate unique tab ID
const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Get current tab ID from sessionStorage or generate new one
const getCurrentTabId = () => {
  if (typeof window === 'undefined') return null;
  let tabId = sessionStorage.getItem('current_tab_id');
  if (!tabId) {
    tabId = generateTabId();
    sessionStorage.setItem('current_tab_id', tabId);
  }
  return tabId;
};

export const loginSlice = createSlice({
  name: 'login',
  initialState: {
    // Legacy single-user state for backward compatibility
    isLoggedIn: false,
    user: {},
    role: null,
    justRegistered: false,
    pendingEmail: null,
    // Multi-tab state
    tabs: {},
    currentTabId: getCurrentTabId(),
  },
  reducers: {
    userlogin(state, action) {
      const { token, user, justRegistered, tabId } = action.payload;
      const currentTabId = tabId || state.currentTabId || getCurrentTabId();

      // Update current tab state
      state.tabs[currentTabId] = {
        isLoggedIn: true,
        user: user || {},
        justRegistered: justRegistered || false,
        token: token,
        role: null,
        pendingEmail: null,
      };

      // Update current tab ID
      state.currentTabId = currentTabId;

      // Update legacy state for backward compatibility
      state.isLoggedIn = true;
      state.user = user || {};
      state.justRegistered = justRegistered || false;
      state.pendingEmail = null;

      try {
        const decoded = jwtDecode(token);
        const role = decoded?.user?.role || decoded?.role || 'user';
        state.tabs[currentTabId].role = role;
        state.role = role; // Legacy compatibility
      } catch (error) {
        console.error('JWT decode error:', error);
        state.tabs[currentTabId].role = null;
        state.role = null;
      }

      // Store token in sessionStorage (tab-specific)
      if (typeof window !== 'undefined' && token) {
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('current_tab_id', currentTabId);
      } else {
        console.warn('Token missing or sessionStorage unavailable');
      }
    },

    userlogout(state, action) {
      const { tabId } = action.payload || {};
      const currentTabId = tabId || state.currentTabId;

      if (currentTabId && state.tabs[currentTabId]) {
        delete state.tabs[currentTabId];
        if (currentTabId === state.currentTabId) {
          state.isLoggedIn = false;
          state.user = {};
          state.role = null;
          state.pendingEmail = null;
          state.justRegistered = false;
          state.currentTabId = null;
        }
      } else {
        state.tabs = {};
        state.isLoggedIn = false;
        state.user = {};
        state.role = null;
        state.pendingEmail = null;
        state.justRegistered = false;
        state.currentTabId = null;
      }

      if (typeof window !== 'undefined') {
        sessionStorage.clear();
      }

      // Release lock after state is cleared
      releaseLogoutLock();
    },
    clearJustRegistered(state, action) {
      const { tabId } = action.payload || {};
      const currentTabId = tabId || state.currentTabId;

      if (currentTabId && state.tabs[currentTabId]) {
        state.tabs[currentTabId].justRegistered = false;
      }

      // Legacy compatibility
      state.justRegistered = false;
    },

    setPendingEmail(state, action) {
      const { email, justRegistered, tabId } = action.payload;
      const currentTabId = tabId || state.currentTabId;

      if (currentTabId && state.tabs[currentTabId]) {
        state.tabs[currentTabId].pendingEmail = { email, justRegistered: justRegistered || false };
      }

      // Legacy compatibility
      state.pendingEmail = { email, justRegistered: justRegistered || false };
    },

    // New action to initialize or switch tabs
    initializeTab(state, action) {
      const { tabId } = action.payload || {};
      const newTabId = tabId || generateTabId();

      state.currentTabId = newTabId;

      if (typeof window !== 'undefined') {
        sessionStorage.setItem('current_tab_id', newTabId);

        // Check if there's an existing auth token for this tab
        const existingToken = sessionStorage.getItem('auth_token');
        if (existingToken && !state.tabs[newTabId]) {
          // Initialize tab with existing session data
          try {
            const decoded = jwtDecode(existingToken);
            state.tabs[newTabId] = {
              isLoggedIn: true,
              user: decoded.user || {},
              role: decoded?.user?.role || decoded?.role || 'user',
              token: existingToken,
              justRegistered: false,
              pendingEmail: null,
            };

            // Update legacy state
            state.isLoggedIn = true;
            state.user = decoded.user || {};
            state.role = decoded?.user?.role || decoded?.role || 'user';
          } catch (error) {
            console.error('Error initializing tab with existing token:', error);
          }
        }
      }
    },
  },
});

// Selectors for multi-tab support
export const getCurrentTabAuth = (state) => {
  const currentTabId = state.auth.currentTabId;
  return currentTabId ? state.auth.tabs[currentTabId] : null;
};

export const getCurrentTabUser = (state) => {
  const tabAuth = getCurrentTabAuth(state);
  return tabAuth?.user || {};
};

export const isCurrentTabLoggedIn = (state) => {
  const tabAuth = getCurrentTabAuth(state);
  return tabAuth?.isLoggedIn || false;
};

export const getCurrentTabRole = (state) => {
  const tabAuth = getCurrentTabAuth(state);
  return tabAuth?.role || null;
};

export const { userlogin, userlogout, clearJustRegistered, setPendingEmail, initializeTab } = loginSlice.actions;
export default loginSlice.reducer;
